/**
 * @file index.tsx
 *
 * @fileoverview Renders login form and handles user login.
 */

/**
 * This is the login page and is accessed by going to "/user/login".
 *
 * The page displays a form for the user to provide email and password and on submit will make a call to log
 * the user in.
 */
import { useMutation } from '@apollo/client';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Link from '~/jobiqo/components/Link';
import OAuthButtons from '~/jobiqo/components/OAuthButtons';
import { PrivacyPolicyLink } from '~/jobiqo/components/PrivacyPolicyLink';
import { getAnonymousUserToken } from '~/jobiqo/lib/session';
import { Error } from '~/jobiqo/models/error';
import GET_TOKEN from '~/jobiqo/components/User/Login/graphql/mutations/login.gql';
import { Branding } from '~/jobiqo/components/Branding';
import Messages from '~/jobiqo/components/Messages';
import RegisterLink from '~/jobiqo/components/User/Login/components/RegisterLink';
import SubmitButton from '~/jobiqo/components/Button/Submit';
import { CenterForm } from '~/vcl/organisms/01-form/01-center';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { PasswordInput } from '~/vcl/atoms/01-forms/03-input/01-password-input';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { ExitIcon } from '~/vcl/icons/Exit';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import RichFormattedMessage from '~/jobiqo/components/RichFormattedMessage';
import { FormProvider, useForm } from 'react-hook-form';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { HeaderLoginPagesSlot } from '~/jobiqo/components/Slots/HeaderLoginPagesSlot';
import { useApplyUtmTags } from '~/jobiqo/components/SourceTracking/hooks/useUtmTagging';
/**
 * Handle the submit of the login form.
 */
const handleLoginSubmit = async (
  email: string,
  password: string,
  performLogin: (options) => Promise<any>,
  setError: any,
  setLoading: any,
  onLoginSuccess: () => void,
  applyUtmTags: (query: Record<string, any>, jwt?: string) => Promise<void>,
  query: Record<string, any>,
) => {
  let errors: Error[] = [];
  const anonymousUserToken = getAnonymousUserToken();

  const { data } = await performLogin({
    fetchPolicy: 'no-cache',
    variables: { email: email, password, anonymousUserToken },
  });

  if (data.JwtToken && data.JwtToken.jwt) {
    const jwt = data.JwtToken.jwt;
    // We are passing in the jwt to NextAuth so that we can keep the error handling in our client.
    const result: any = await signIn('sign-in', {
      redirect: false,
      jwt: jwt,
    });
    if (result?.status === 200) {
      await applyUtmTags(query, jwt);
      onLoginSuccess();
    } else {
      errors = [...errors, { message: 'There was an error setting the user.' }];
    }
  } else {
    errors = data.JwtToken.errors;
  }

  // Check for any errors and update internal state if so.
  if (errors) {
    setError(errors);
    setLoading(false);
  }
};

/**
 * Cleans the errors when the user click on closing message.
 */
const cleanErrors = (setError: any, setLoading: any) => {
  setError([]);
  setLoading(false);
};

export interface LoginFormProps {
  /**
   * Successfull login callback.
   */
  onLoginSuccess: () => void;
}

export const LoginForm = ({ onLoginSuccess }: LoginFormProps) => {
  const intl = useIntl();
  const router = useRouter();
  const frontendConfig = useFrontendConfig();
  const READ_ONLY_MODE = frontendConfig.APP.READ_ONLY_MODE;
  const frontendTheme = getFrontendTheme(frontendConfig);
  const [errors, setError] = useState<Error[]>([]);
  const [loading, setLoading] = useState(false);
  const { applyUtmTags } = useApplyUtmTags();

  const methods = useForm();
  const { register, handleSubmit } = methods;

  const [
    performLogin,
    { data: userLoginData, loading: userLoginLoading },
  ] = useMutation(GET_TOKEN);

  const isSubmittingLogin =
    userLoginLoading ||
    (userLoginData && userLoginData.JwtToken && userLoginData.JwtToken.jwt);

  const onSubmit = async (data) => {
    if (!READ_ONLY_MODE) {
      handleLoginSubmit(
        data.email,
        data.password,
        performLogin,
        setError,
        setLoading,
        onLoginSuccess,
        applyUtmTags,
        router.query,
      );
    }
  };

  return (
    <CenterBox>
      <HeaderLoginPagesSlot />
      <Messages noMargin={true} />
      <Spacing size="medium" />
      {errors.length > 0 && (
        <>
          <MessageAlert
            type="assertive"
            alertStyle="danger"
            onClose={() => cleanErrors(setError, setLoading)}
          >
            <ul data-test-id="user-login-errors">
              {errors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </MessageAlert>
          <Spacing size="medium" />
        </>
      )}
      {!errors && loading && (
        <div>
          <FormattedMessage
            id="login_loading"
            defaultMessage="Logging you in..."
          />
        </div>
      )}
      {!loading && (
        <div className="user-login-form">
          <FormProvider {...methods}>
            <CenterForm
              onSubmit={handleSubmit(onSubmit)}
              method="POST"
              data-testid="login-form"
            >
              <HideDesktop>
                <Spacing />
              </HideDesktop>
              <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
              <Spacing />
              <h1>
                <FormattedMessage id="login" defaultMessage={`Login`} />
              </h1>
              <Spacing />
              {READ_ONLY_MODE && (
                <div
                  style={{
                    maxWidth: '400px',
                  }}
                >
                  <Spacing marginBottom={6}>
                    <MessageAlert alertStyle="danger">
                      <RichFormattedMessage
                        id="login_block_readonly"
                        defaultMessage="Login is blocked temporarily while the site is in maintenance mode."
                      />
                    </MessageAlert>
                  </Spacing>
                </div>
              )}
              <Input
                {...register('email', { required: true })}
                placeholder={intl.formatMessage({
                  id: 'login_email',
                  defaultMessage: 'Email',
                })}
                name="email"
                type="text"
              />
              <PasswordInput
                {...register('password', { required: true })}
                placeholder={intl.formatMessage({
                  id: 'login_password',
                  defaultMessage: 'Password',
                })}
                name="password"
              />
              <Spacing />
              <SubmitButton
                isSubmitting={isSubmittingLogin}
                data-testid="login-submit-btn"
                className="button-submit"
                disabled={isSubmittingLogin || READ_ONLY_MODE}
              >
                <>
                  <ExitIcon title="Login" aria-hidden="true" />{' '}
                  <span>
                    <FormattedMessage
                      id="login_submit"
                      defaultMessage={`Login with your email`}
                    />
                  </span>
                </>
              </SubmitButton>
              <Spacing />
              <OAuthButtons />
            </CenterForm>
          </FormProvider>
          <Spacing size="medium" />
          <RegisterLink />
          <Spacing size="medium" />
          <Text fontSize={2} textAlign="center">
            <FormattedMessage
              id="login_forgot_password"
              defaultMessage="Forgot your password?"
            />{' '}
            <Link
              data-test-id="password-reset-link"
              href="/user/reset-password"
            >
              <FormattedMessage
                id="reset_password"
                defaultMessage="Reset password"
              />
            </Link>
          </Text>
        </div>
      )}
      <PrivacyPolicyLink />
    </CenterBox>
  );
};

export default LoginForm;
